import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '@stores/auth.store';
import { refreshTokensSingleton } from '@services/refresh.service';
import { getRealtimeSocket, reconnectWithNewToken } from '@services/realtime.socket';

const CONNECT_TIMEOUT_MS = 20000;
const MAX_SOCKET_REFRESH_RETRIES = 3;

/**
 * Socket connection status for UI (Live / Connecting / Offline).
 * On auth failure, refreshes the token and reconnects the existing socket
 * (preserving all event listeners).
 */
export function useChatSocketStatus() {
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState(null);
    const timeoutRef = useRef(null);
    const retryCountRef = useRef(0);

    const tryRefreshAndReconnect = () => {
        const refreshTokenValue = useAuthStore.getState().refreshToken;
        if (!refreshTokenValue) return;

        if (retryCountRef.current >= MAX_SOCKET_REFRESH_RETRIES) {
            return;
        }
        retryCountRef.current += 1;
        setConnecting(true);
        setError(null);

        refreshTokensSingleton(
            () => useAuthStore.getState().refreshToken,
            useAuthStore.setState,
            useAuthStore.getState().logout,
        ).then((tokens) => {
            if (!tokens?.accessToken) return;
            reconnectWithNewToken();
        }).catch((err) => {
            console.error('Socket auth refresh error:', err);
        });
    };

    useEffect(() => {
        const socket = getRealtimeSocket();
        if (!socket) {
            setConnected(false);
            setConnecting(false);
            setError(null);
            return () => {};
        }

        setConnecting(!socket.connected);
        setConnected(socket.connected);
        setError(null);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        const onConnect = () => {
            retryCountRef.current = 0;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            setConnected(true);
            setConnecting(false);
            setError(null);
        };

        const onDisconnect = (reason) => {
            setConnected(false);
            setConnecting(false);
            if (reason === 'io server disconnect' || reason === 'io client disconnect') {
                tryRefreshAndReconnect();
            }
        };

        const onConnectError = (err) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            setConnected(false);
            setConnecting(false);
            const msg = err?.message ?? '';
            if (msg.includes('AUTH_REQUIRED') || msg.includes('AUTH_INVALID')) {
                setError('Authentication failed');
                tryRefreshAndReconnect();
            } else {
                setError(msg || 'Connection failed');
            }
        };

        if (!socket.connected) {
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                setConnecting(false);
                setError((e) => e || 'Connection timeout');
                tryRefreshAndReconnect();
            }, CONNECT_TIMEOUT_MS);
        }

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
        };
    }, []);

    const socket = getRealtimeSocket();
    const actualConnected = socket?.connected ?? false;
    return {
        connected: actualConnected || connected,
        connecting: actualConnected ? false : connecting,
        error: actualConnected ? null : error,
    };
}
